// pages/GroupProfile.tsx
import React, { useState, useRef, useCallback } from "react";
import { ContentHeader } from "@components";
import DataTable from "../components/datatable/DataTable";
import GroupProfileModal from "../components/modal/GroupProfileModal";
import API from "../api";
import toastr from "toastr";

interface GroupProfile {
  id: number;
  category_name: string;
  description?: string;
  notes?: string;
  amount?: number;
  created?: string;
  modified?: string;
  items?: any[];
}

const GroupProfile: React.FC = () => {
  const [showModal, setShowModal]         = useState(false);
  const [modalMode, setModalMode]         = useState<"add" | "edit">("add");
  const [selectedProfile, setSelectedProfile] = useState<GroupProfile | null>(null);
  const [loadingEdit, setLoadingEdit]     = useState(false);
  const tableRef = useRef<any>(null);

  // ── Fetch list (lab-scoped via auth token) ─────────────────────────────
  const fetchGroupProfiles = useCallback(
    async (page: number, search: string, sortBy = "category_name", sortOrder = "asc") => {
      try {
        const res = await API.get(
          `groupProfiles/getGroupProfiles?page=${page}&pageSize=25` +
          `&search=${encodeURIComponent(search)}&sortBy=${sortBy}&sortOrder=${sortOrder}`
        );
        return { data: res.data.data, total: res.data.total };
      } catch {
        toastr.error("Failed to fetch group profiles");
        return { data: [], total: 0 };
      }
    },
    []
  );

  // ── Open Add modal ─────────────────────────────────────────────────────
  const handleAdd = useCallback(() => {
    setModalMode("add");
    setSelectedProfile(null);
    setShowModal(true);
  }, []);

  // ── Open Edit modal (fetch full data including items) ──────────────────
  const handleEdit = useCallback(async (profile: GroupProfile) => {
    setLoadingEdit(true);
    try {
      const res = await API.get(`groupProfiles/getGroupProfileById/${profile.id}`);
      setSelectedProfile(res.data.data);
      setModalMode("edit");
      setShowModal(true);
    } catch {
      toastr.error("Failed to load profile details");
    } finally {
      setLoadingEdit(false);
    }
  }, []);

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`))
      return;
    try {
      await API.delete(`groupProfiles/deleteGroupProfile/${id}`);
      toastr.success("Group profile deleted successfully");
      tableRef.current?.refetch();
    } catch {
      toastr.error("Failed to delete group profile");
    }
  }, []);

  const handleModalClose = useCallback(() => setShowModal(false), []);

  const handleSuccess = useCallback(() => {
    setShowModal(false);
    tableRef.current?.refetch();
  }, []);

  // ── Columns ────────────────────────────────────────────────────────────
  const columns = [
    {
      header: "#",
      id: "rowIndex",
      cell: ({ row }: any) => row.index + 1,
    },
    {
      header: "Profile Name",
      accessorKey: "category_name",
    },
    {
      header: "Description",
      accessorKey: "description",
      cell: ({ row }: any) => row.original.description || "-",
    },
    {
      header: "Amount",
      accessorKey: "amount",
      cell: ({ row }: any) => `₹${Number(row.original.amount ?? 0).toFixed(2)}`,
    },
    {
      header: "Created",
      accessorKey: "created",
      cell: ({ row }: any) => row.original.created || "-",
    },
    {
      header: "Last Modified",
      accessorKey: "modified",
      cell: ({ row }: any) => row.original.modified || "-",
    },
    {
      header: "Action",
      id: "actions",
      cell: ({ row }: any) => {
        const data: GroupProfile = row.original;
        return (
          <div className="d-flex justify-content-center gap-1">
            <button
              className="btn btn-light"
              onClick={() => handleEdit(data)}
              title="Edit"
              disabled={loadingEdit}
            >
              <i className="fa fa-edit" />
            </button>
            <button
              className="btn btn-light"
              onClick={() => handleDelete(data.id, data.category_name)}
              title="Delete"
            >
              <i className="fa fa-trash" />
            </button>
          </div>
        );
      },
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <ContentHeader title="Group Profile" />
        <button className="btn btn-light" onClick={handleAdd}>
          <i className="fa fa-plus me-1" /> Create New
        </button>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchGroupProfiles}
                queryKey={["groupProfiles"]}
              />
            </div>
          </div>
        </div>
      </section>

      <GroupProfileModal
        show={showModal}
        onClose={handleModalClose}
        mode={modalMode}
        profileData={selectedProfile}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default GroupProfile;